import { useRef, useState } from "react";
import { ControlPanel } from "@/components/controls/ControlPanel.tsx";
import { CountdownBar } from "@/components/countdown/CountdownBar.tsx";
import { PlanView } from "@/components/plan/PlanView.tsx";
import { FilteredRoadProvider, useFilteredRoad } from "@/lib/filters/context.tsx";
import { PlanProvider } from "@/lib/plan/context.tsx";
import { usePhaseTheme } from "@/lib/theme/usePhaseTheme.ts";
import { useCountdownClock } from "@/state/countdown.ts";
import styles from "./AppShell.module.css";
import { Footer } from "./Footer.tsx";
import { Header } from "./Header.tsx";
import { RoadList } from "./RoadList.tsx";

type View = "road" | "plan";

export function AppShell() {
  return (
    <FilteredRoadProvider>
      <PlanProvider>
        <Shell />
      </PlanProvider>
    </FilteredRoadProvider>
  );
}

function Shell() {
  const roadRef = useRef<HTMLElement | null>(null);
  const [view, setView] = useState<View>("road");
  const { visible } = useFilteredRoad();
  // Filtering adds and removes the [data-phase] sections the theme observer
  // watches, so it has to re-register whenever the surviving set changes.
  usePhaseTheme(roadRef, visible);
  // The one interval in the app: every ticking thing in the countdown reads
  // the clock this starts (PLAN §3).
  useCountdownClock();

  return (
    <div className={styles.shell}>
      <Header />
      <CountdownBar />
      <div className={styles.body}>
        <ControlPanel />
        <main className={styles.main} ref={roadRef}>
          {/* The filters drive both views, so the switch sits above the
           * content rather than in the panel — it changes how you're looking
           * at the same filtered set, not what's in it. */}
          <div className={styles.viewSwitch} role="tablist" aria-label="View">
            <button
              type="button"
              role="tab"
              aria-selected={view === "road"}
              className={view === "road" ? styles.viewOn : styles.view}
              onClick={() => setView("road")}
            >
              Road
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === "plan"}
              className={view === "plan" ? styles.viewOn : styles.view}
              onClick={() => setView("plan")}
            >
              Plan
            </button>
          </div>
          {view === "road" ? <RoadList /> : <PlanView />}
        </main>
      </div>
      <Footer />
    </div>
  );
}
